import React, { useEffect } from "react";
import { connect } from "react-redux";
import CurrencyFormatter from "currency-formatter";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import "../../../Dashboard/dashboard.css";
import { rowStyle } from "../../../Dashboard/customStyles";
import {
  generateDiscountedCashFlowMethod,
  updateDiscountCashFlowMethod
} from "../../../../store/modules/discountedCFM/actions";
import FinancialHandler from "../../../../handlers/FinancialHandler";
import { styled } from "@mui/material/styles";
import BVAInput from "../../../../components/BVAInput/BVAInput";
import {THREE_STAGE_METHOD} from "../../../../models/valuation/DiscountedCashFlowMethod";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  minWidth: "7rem",
  maxWidth: "7rem",
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const DiscountedCFM = ({
  appState,
  discountedCashFlowMethod,
  dispatchGenerateDiscountedCashFlowMethod,
  dispatchUpdateDiscountCashFlowMethod
}) => {
  // Mounted
  useEffect(() => {
    dispatchGenerateDiscountedCashFlowMethod()
  }, [])

  // Methods
  const handleChangeSeries = (value, index, type) => {
    const data = discountedCashFlowMethod[type]
    data[index] = CurrencyFormatter.unformat(value, { code: "USD" })

    dispatchUpdateDiscountCashFlowMethod({ [type]: data })
  };

  const handleChangeYears = (e) => {
    dispatchUpdateDiscountCashFlowMethod({ totalYears: e.target.value })
  };

  const handleChangeDiscountFactor = (e) => {
    dispatchUpdateDiscountCashFlowMethod({ discountFactor: e.target.value })
  };

  const handleChangeMethod = (e) => {
    dispatchUpdateDiscountCashFlowMethod({ method: e.target.value })
  };

  const handleChangeLongTermGrowthRate = (value) => {
    dispatchUpdateDiscountCashFlowMethod({ terminalYear: {
      longTermGrowthRate: CurrencyFormatter.unformat(value, { code: "USD" })
    }})
  };

  const handleChangeNote = (value) => {
    let analystNote = discountedCashFlowMethod?.analystNote;
    analystNote[discountedCashFlowMethod?.method] = value;
    dispatchUpdateDiscountCashFlowMethod({ analystNote })
  };

  const handleOnClickGPT = async (type) => {
    handleChangeNote("Generating...");

    let payload =
      `\nHere are forecasts for several years of increasing free cash flow and those present values.` +
      `\nFinancial years: ` + discountedCashFlowMethod?.yearsArray?.join(", ") +
      `\nForecasted free cash flow: ` + discountedCashFlowMethod?.freeCashFlow?.map((val) => CurrencyFormatter.format(val, { code: "USD", symbol: "$" })).join(", ") +
      `\nPresent value factors: ` + discountedCashFlowMethod?.dividePresent?.map((val) => val.toFixed(2)).join(", ") +
      `\nPresent values: ` + discountedCashFlowMethod?.presentValue?.map((val) => CurrencyFormatter.format(val, { code: "USD", symbol: "$" })).join(", ") +
      `\nBelow is the company's forecasted terminal year value calculation. It is calculated using a perpetual growth model.` +
      `\nThe forecasted free cash flow is ${Number(discountedCashFlowMethod?.forecastedFreeCashFlow).toFixed(2)}$.` +
      `\nDividing this by the capitalization rate ${Number(discountedCashFlowMethod?.terminalYear?.divideCapitalizationRate * 100).toFixed(2)}%, we get the terminal year value ${Number(discountedCashFlowMethod?.terminalYearValue).toFixed(2)}$.` +
      `\nIt is divided by present value factor ${Number(discountedCashFlowMethod?.terminalDividePresent).toFixed(2)} and thus the present value for terminal year is ${Number(discountedCashFlowMethod?.terminalPresentValue).toFixed(2)}$.` +
      `\nBelow is the calculation for the delta of capitalization value. The sum of all the present values of free cash flow and the present value of the terminal year gives the estimated current value of the business. It refers to the difference between this DCF valuation and the capitalized earnings.` +
      `\nValue of the business: ${Number(discountedCashFlowMethod?.valueSubjectCompany).toFixed(2)}$.` +
      `\nCapitalized earnings: ${Number(discountedCashFlowMethod?.afterTaxCapitalizedEarningsOfSubjectCompany).toFixed(2)}$.` +
      `\nDelta of capitalization value: ${Number(discountedCashFlowMethod?.delta).toFixed(2)}$.` +
      `\nPlease analyze the company's situation.`;

    const note = await FinancialHandler.fetchGPTSimpleNoteGeneration(payload);
    handleChangeNote(note);
  }

  return (
    <>
      <h2 className="sectionHead">Discounted Cash Flow Method</h2>
      <div className="dashboard-content">
        <div className="data-title-container">
          <div></div>

          <div className="data-title">
            <h6>{appState?.companyData?.name}</h6>
            <p>Discounted Cash Flow Method </p>
          </div>
        </div>
        <div style={{ paddingTop: "35px" }}></div>
        <div style={rowStyle.baseRow}>
          <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="demo-simple-select-standard-label">
              Years
            </InputLabel>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={discountedCashFlowMethod?.totalYears}
              onChange={handleChangeYears}
              label="Years"
            >
              <MenuItem value={5}>Five</MenuItem>
              <MenuItem value={7}>Seven</MenuItem>
              <MenuItem value={10}>Ten</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="demo-simple-select-standard-label">
              Discount Factor
            </InputLabel>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={discountedCashFlowMethod?.discountFactor}
              onChange={handleChangeDiscountFactor}
              label="Discount Factor"
            >
              <MenuItem value={0.5}>Mid-Year Discount Factor</MenuItem>
              <MenuItem value={1}>Annual Discount Factor</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="dcf-method-inputlabel">DCF Method</InputLabel>
            <Select
              labelId="dcf-method-select-standard-label"
              id="dcf-method-select"
              value={discountedCashFlowMethod?.method}
              onChange={handleChangeMethod}
              label="DCF Method"
            >
              <MenuItem value={1}>1 Stage DCF Method</MenuItem>
              <MenuItem value={2}>2 Stage DCF Method</MenuItem>
              <MenuItem value={3}>3 Stage DCF Method</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Discount Factor</InputLabel>
        </div>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.discountFactorArray?.map((discountFactor) => (
            <Item>{CurrencyFormatter.format(discountFactor, { code: "USD", symbol: ""})}</Item>
          ))}
        </Stack>
        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Year</InputLabel>
        </div>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.yearsArray?.map((year) => (
            <Item>{year}</Item>
          ))}
        </Stack>
        {/* <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Free Cash Flow</InputLabel>
        </div>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.yearsArray?.map((year) => (
            <Item>Amount ($)</Item>
          ))}
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.freeCashFlow?.map((value) => (
            <Item>{CurrencyFormatter.format(value, { code: "USD", symbol: "" })}</Item>
          ))}
        </Stack> */}
        {discountedCashFlowMethod?.method === THREE_STAGE_METHOD && (
          <>
            <div style={{ alignSelf: "flex-start" }}>
              <InputLabel>EBIT</InputLabel>
            </div>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 1, md: 2 }}
            >
              {discountedCashFlowMethod?.EBIT?.map((value, index) => (
                <Item>
                  <BVAInput
                    startAdornment
                    startAdornmentLabel="$"
                    variant="standard"
                    textAlign="center"
                    InputProps={{ disableUnderline: true }}
                    onChange={(_value) => handleChangeSeries(_value, index, "EBIT")}
                    value={value || ""}
                  ></BVAInput>
                </Item>
              ))}
            </Stack>
            <div style={{ alignSelf: "flex-start" }}>
              <InputLabel>EBIT ( 1 - T )</InputLabel>
            </div>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 1, md: 2 }}
            >
              {discountedCashFlowMethod?.EBI?.map((value, index) => (
                <Item>{CurrencyFormatter.format(value, { code: "USD", symbol: "$" })}</Item>
              ))}
            </Stack>
            <div style={{ alignSelf: "flex-start" }}>
              <InputLabel>Reinvestment</InputLabel>
            </div>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 1, md: 2 }}
            >
              {discountedCashFlowMethod?.reinvestment?.map((value, index) => (
                <Item>
                  <BVAInput
                    startAdornment
                    startAdornmentLabel="$"
                    variant="standard"
                    textAlign="center"
                    InputProps={{ disableUnderline: true }}
                    onChange={(_value) => handleChangeSeries(_value, index, "reinvestment")}
                    value={value || ""}
                  ></BVAInput>
                </Item>
              ))}
            </Stack>
          </>
        )}
        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Forecasted Future Benefit Streams</InputLabel>
        </div>
        {/* <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.yearsArray?.map((year) => (
            <Item>Amount ($)</Item>
          ))}
        </Stack> */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.freeCashFlow?.map((value) => (
            <Item>{CurrencyFormatter.format(value, { code: "USD", symbol: "$" })}</Item>
          ))}
        </Stack>

        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Divide by Present Value Factor</InputLabel>
        </div>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.dividePresent?.map((value) => (
            <Item>{CurrencyFormatter.format(value, { code: "USD", symbol: "", decimalDigits: 5 })}</Item>
          ))}
        </Stack>

        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel>Present Value</InputLabel>
        </div>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1, md: 2 }}
        >
          {discountedCashFlowMethod?.presentValue?.map((value) => (
            <Item>{CurrencyFormatter.format(value, { code: "USD", symbol: "$" })}</Item>
          ))}
        </Stack>

        <div style={{ paddingTop: "35px" }}></div>

        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel style={{ fontWeight: "bold" }}>
            Terminal Value Calculations:
          </InputLabel>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Growth Rate:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              decimalScale={3}
              endAdornment
              endAdornmentLabel="%"
              onChange={handleChangeLongTermGrowthRate}
              value={discountedCashFlowMethod?.averageGrowthRate}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Final Year Free Cash Flow:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.finalYearFreeCashFlow}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>1+ Long-Term Sustainable Growth Rate:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              decimalScale={3}
              startAdornment
              startAdornmentLabel
              value={discountedCashFlowMethod?.terminalYear?.longTermGrowthRateMultiplier}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Forecasted Free Cash Flow:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.forecastedFreeCashFlow}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Divided by The Capitalization Rate:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              decimalScale={3}
              startAdornment
              startAdornmentLabel="÷"
              value={discountedCashFlowMethod?.terminalYear?.divideCapitalizationRate}
            />
          </div>
        </div>

        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Terminal Year Value:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.terminalYearValue}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel>Divide by Present Value Factor:</InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="÷"
              value={discountedCashFlowMethod?.terminalDividePresent}
            />
          </div>
        </div>
        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel style={{ fontWeight: "bold" }}>
              Present Value:
            </InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.terminalPresentValue}
            />
          </div>
        </div>

        <div style={{ paddingTop: "35px" }}></div>

        <div style={{ alignSelf: "flex-start" }}>
          <InputLabel style={{ fontWeight: "bold" }}>
            Final Calculations:
          </InputLabel>
        </div>

        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel style={{ fontWeight: "bold" }}>
              Value of Subject Company:
            </InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.valueSubjectCompany}
            />
          </div>
        </div>

        <div style={rowStyle.baseRow}>
          <div style={rowStyle.labelRow}>
            <InputLabel style={{ fontWeight: "bold" }}>
              Delta of Capitalization Value:
            </InputLabel>
          </div>
          <div style={rowStyle.fieldRow}>
            <BVAInput
              disabled
              size="small"
              startAdornment
              startAdornmentLabel="$"
              value={discountedCashFlowMethod?.delta}
            />
          </div>
        </div>
        <div className="data-bottom mt-10 mr-20 pr-20" style={{ width: "45em" }}>
          <h5 className="noteHead">
            Analyst Note&nbsp;&nbsp;&nbsp;
            <button onClick={() => handleOnClickGPT()}>🤖</button>
          </h5>
          <textarea
            className="analystNote"
            rows={3}
            value={discountedCashFlowMethod?.analystNote[discountedCashFlowMethod?.method]}
            onChange={(e) => handleChangeNote(e.target.value)}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    discountedCashFlowMethod: state.discountedCFM.discountedCashFlowMethod,
    taxes: state.taxes,
    appState: state.appState,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatchGenerateDiscountedCashFlowMethod() {
    dispatch(generateDiscountedCashFlowMethod())
  },

  dispatchUpdateDiscountCashFlowMethod(props) {
    dispatch(updateDiscountCashFlowMethod(props))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(DiscountedCFM);
